import type { PublicBuildingState as BuildingState } from "@point/core";
import { type CSSProperties, useId } from "react";
import { BUILDING_H_REF, getBuildingHeightPixels, getBuildingScaleY, getIsoFootprint, hasIntrinsicWireHeight } from "./buildingGeometry.ts";
import { isoToScreen } from "./iso.ts";
import { BP, HEALTH_STROKE } from "./theme.ts";

function captionLines(app: { tagline?: string; concept?: string; name: string }): string {
	const t = app.tagline?.trim();
	if (t) return t.length > 96 ? `${t.slice(0, 94)}…` : t;
	const c = app.concept?.trim();
	if (c) return c.length > 96 ? `${c.slice(0, 94)}…` : c;
	return app.name.trim() || "—";
}

function diamondPoints(cx: number, cy: number, fw: number, fh: number): string {
	return [
		[cx, cy - fh],
		[cx + fw, cy],
		[cx, cy + fh],
		[cx - fw, cy],
	]
		.map(([x, y]) => `${x},${y}`)
		.join(" ");
}

/** West + north facets read depth in 2:1 iso without full 3D math. */
function facadeShadePolys(cx: number, cy: number, footW: number, footH: number, h: number): { west: string; north: string } {
	const west = [
		[cx - footW, cy],
		[cx, cy - footH],
		[cx, cy - h - footH],
		[cx - footW, cy - h],
	]
		.map(([x, y]) => `${x},${y}`)
		.join(" ");
	const north = [
		[cx, cy - footH],
		[cx + footW, cy],
		[cx + footW, cy - h],
		[cx, cy - h - footH],
	]
		.map(([x, y]) => `${x},${y}`)
		.join(" ");
	return { west, north };
}

function floorBandCount(h: number, sparse: boolean): number {
	if (sparse) return h >= 18 ? 1 : 0;
	if (h < 15) return 0;
	if (h < 24) return 2;
	if (h < 38) return 3;
	return 4;
}

type WireBoxProps = {
	cx: number;
	cy: number;
	footW: number;
	footH: number;
	h: number;
	stroke: string;
	strokeWidth: number;
	dash?: string;
	fillRoof?: string;
	sparseDetail?: boolean;
};

function WireBox({ cx, cy, footW, footH, h, stroke, strokeWidth, dash, fillRoof, sparseDetail = false }: WireBoxProps) {
	const base = diamondPoints(cx, cy, footW, footH);
	const roof = diamondPoints(cx, cy - h, footW, footH);
	const { west, north } = facadeShadePolys(cx, cy, footW, footH, h);
	const bands = floorBandCount(h, sparseDetail);
	const fillWest = sparseDetail ? 0.04 : 0.07;
	const fillNorth = sparseDetail ? 0.055 : 0.1;
	const bandOp = sparseDetail ? 0.32 : 0.48;
	const capShrink = 0.9;
	const showCap = h >= 12 && !sparseDetail;

	return (
		<g className="bp-building" shapeRendering="geometricPrecision">
			<g style={{ pointerEvents: "none" }}>
				<polygon points={west} fill={stroke} fillOpacity={fillWest} stroke="none" />
				<polygon points={north} fill={stroke} fillOpacity={fillNorth} stroke="none" />
				{Array.from({ length: bands }, (_, band) => {
					const t = (band + 1) / (bands + 1);
					return (
						<polygon
							key={`band-${t}`}
							points={diamondPoints(cx, cy - t * h, footW, footH)}
							fill="none"
							stroke={stroke}
							strokeWidth={strokeWidth * 0.42}
							strokeOpacity={bandOp}
							strokeLinejoin="miter"
							strokeDasharray={dash}
						/>
					);
				})}
			</g>
			<polygon points={base} fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinejoin="miter" strokeDasharray={dash} />
			<line x1={cx - footW} y1={cy} x2={cx - footW} y2={cy - h} stroke={stroke} strokeWidth={strokeWidth * 0.85} strokeDasharray={dash} />
			<line x1={cx + footW} y1={cy} x2={cx + footW} y2={cy - h} stroke={stroke} strokeWidth={strokeWidth * 0.85} strokeDasharray={dash} />
			<line x1={cx} y1={cy - footH} x2={cx} y2={cy - h - footH} stroke={stroke} strokeWidth={strokeWidth * 0.85} strokeDasharray={dash} />
			<line x1={cx} y1={cy + footH} x2={cx} y2={cy - h + footH} stroke={stroke} strokeWidth={strokeWidth * 0.85} strokeDasharray={dash} />
			<polygon
				points={roof}
				fill={fillRoof ?? "none"}
				fillOpacity={fillRoof ? 1 : 0}
				stroke={stroke}
				strokeWidth={strokeWidth}
				strokeLinejoin="miter"
				strokeDasharray={dash}
			/>
			{showCap ? (
				<polygon
					points={diamondPoints(cx, cy - h, footW * capShrink, footH * capShrink)}
					fill="none"
					stroke={stroke}
					strokeWidth={strokeWidth * 0.5}
					strokeOpacity={0.55}
					strokeLinejoin="miter"
				/>
			) : null}
		</g>
	);
}

type BlueprintBuildingProps = {
	building: BuildingState;
	selected: boolean;
	appearIndex?: number;
};

const HR = BUILDING_H_REF;

export function BlueprintBuilding({ building, selected, appearIndex = 0 }: BlueprintBuildingProps) {
	const rawId = useId();
	const { app, phase, gridPos, health } = building;
	const [gx, gz] = gridPos;
	const { x: cx, y: cy } = isoToScreen(gx, gz);

	const { variant, footW, footH } = getIsoFootprint(app._id);
	const stroke = building.color;
	const sw = selected ? 2.35 : 1.05;
	const dash = phase === "new" ? "4 3" : undefined;
	const healthColor = HEALTH_STROKE[health] ?? HEALTH_STROKE.healthy;

	const hatchId = `hatch-${rawId.replace(/:/g, "")}`;
	const shipped = phase === "live" || phase === "growing";
	const roofFill = shipped ? `url(#${hatchId})` : undefined;
	const caption = captionLines(app);
	const labelY = cy + footH + (phase === "new" ? 16 : 6);
	const sparseDetail = phase === "new";

	const hTarget = getBuildingHeightPixels(building);
	const scaleY = getBuildingScaleY(building);
	const intrinsic = hasIntrinsicWireHeight(building);
	/** For concept lots, use real extrusion height so scaleY does not squash the footprint diamond. */
	const hW = intrinsic ? hTarget : HR;

	const riseStyle: CSSProperties = intrinsic
		? { willChange: "auto" }
		: {
				transform: `translate(${cx}px, ${cy}px) scale(1, ${scaleY}) translate(${-cx}px, ${-cy}px)`,
				transition: "transform 0.78s cubic-bezier(0.33, 1, 0.68, 1)",
				willChange: "transform",
			};

	const popDelayStyle: CSSProperties = {
		animationDelay: `${appearIndex * 38}ms`,
	};

	return (
		<g className="bp-building-appear pointer-events-none" style={popDelayStyle}>
			{shipped ? (
				<defs>
					<pattern id={hatchId} patternUnits="userSpaceOnUse" width={5} height={5} patternTransform="rotate(45)">
						<line x1={0} y1={0} x2={0} y2={5} stroke={stroke} strokeWidth={0.55} opacity={0.22} />
					</pattern>
				</defs>
			) : null}

			<circle className="bp-beacon" cx={cx} cy={cy - hTarget - 14} r={3.25} fill="none" stroke={healthColor} strokeWidth={1} />

			<g className="bp-rise" style={riseStyle}>
				{selected ? (
					<circle
						className="bp-sel-ring"
						cx={cx}
						cy={cy - hTarget * 0.5}
						r={Math.max(footW, footH) + 14}
						fill="none"
						stroke={BP.accent}
						strokeWidth={0.65}
						strokeDasharray="3 4"
						opacity={0.55}
					/>
				) : null}

				{variant === 1 ? (
					<>
						<WireBox
							cx={cx}
							cy={cy}
							footW={footW}
							footH={footH}
							h={hW}
							stroke={stroke}
							strokeWidth={sw}
							dash={dash}
							fillRoof={roofFill}
							sparseDetail={sparseDetail}
						/>
						<WireBox
							cx={cx + footW * 0.85}
							cy={cy + footH * 0.55}
							footW={footW * 0.55}
							footH={footH * 0.55}
							h={hW * 0.55}
							stroke={stroke}
							strokeWidth={sw * 0.9}
							dash={dash}
							fillRoof={roofFill}
							sparseDetail={sparseDetail}
						/>
					</>
				) : variant === 3 ? (
					<>
						<WireBox
							cx={cx}
							cy={cy}
							footW={footW}
							footH={footH}
							h={hW}
							stroke={stroke}
							strokeWidth={sw}
							dash={dash}
							fillRoof={roofFill}
							sparseDetail={sparseDetail}
						/>
						<WireBox
							cx={cx}
							cy={cy - hW * 0.45}
							footW={footW * 0.65}
							footH={footH * 0.65}
							h={hW * 0.4}
							stroke={stroke}
							strokeWidth={sw * 0.85}
							dash={dash}
							fillRoof={roofFill}
							sparseDetail={sparseDetail}
						/>
					</>
				) : (
					<WireBox
						cx={cx}
						cy={cy}
						footW={footW}
						footH={footH}
						h={hW}
						stroke={stroke}
						strokeWidth={sw}
						dash={dash}
						fillRoof={roofFill}
						sparseDetail={sparseDetail}
					/>
				)}
			</g>

			<foreignObject x={cx - 52} y={labelY} width={104} height={28} className="pointer-events-none overflow-visible">
				<p className="m-0 line-clamp-2 text-center font-sans text-[5.25px] leading-snug text-[var(--color-text-muted)]">{caption}</p>
			</foreignObject>

			{phase === "new" ? (
				<text x={cx} y={cy + 2} textAnchor="middle" fill={BP.muted} fontSize={12} fontFamily='ui-serif, "Iowan Old Style", Georgia, serif' fontStyle="italic">
					?
				</text>
			) : null}

			<ellipse
				className="pointer-events-auto"
				cx={cx}
				cy={cy - hTarget * 0.52}
				rx={footW * 2.5}
				ry={Math.max(18, hTarget * 0.72 + footH * 2.4)}
				fill="rgba(0,0,0,0)"
				stroke="none"
				style={{ cursor: "pointer" }}
				pointerEvents="all"
			/>
		</g>
	);
}
